// Tooltip.tsx
import React, { FunctionComponent, ReactNode } from 'react';
import styled from 'styled-components';
import { Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps } from '@mui/material';
import Colors, { blendWithWhite } from '../global/Colors';
import Fonts from '../global/Fonts';

const TooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MainTextOrContent = styled.div`
  font-family: ${Fonts.lexendMedium.fontFamily};
  font-weight: ${Fonts.lexendMedium.fontWeight};
  font-size: 14px;
  line-height: 1.2em;
  vertical-align: center;
  margin: 0 auto;
`;

const SubText = styled.div<{ $colorMode: 'light' | 'dark' }>`
  font-family: ${Fonts.lexendLight.fontFamily};
  font-weight: ${Fonts.lexendLight.fontWeight};
  font-size: 12px;
  line-height: 1.2em;
  color: ${({ $colorMode }) => ($colorMode === 'light' ? Colors.black : Colors.white)};
  vertical-align: center;
  margin: 0 auto;
`;

const StyledTooltip = styled(({ className, $colorMode, $hasMainText, $hasContent, ...props }: { className?: string; $colorMode: 'light' | 'dark'; $hasMainText: boolean; $hasContent: boolean } & Omit<MuiTooltipProps, 'classes'>) => (
  <MuiTooltip {...props} classes={{ popper: className }} />
))<{ $colorMode: 'light' | 'dark'; $hasMainText: boolean; $hasContent: boolean }>`
  & .MuiTooltip-tooltip {
    background-color: ${({ $colorMode }) => ($colorMode === 'light' ? Colors.grey1 : Colors.grey9)};
    color: ${({ $colorMode }) => ($colorMode === 'light' ? Colors.grey9 : Colors.grey1)};
    border-radius: 8px;
    border: 1px solid ${({ $colorMode }) => ($colorMode === 'light' ? blendWithWhite(Colors.grey2, 0.8) : Colors.grey8)};
    // box-shadow: 0px 2px 4px ${({ $colorMode }) => ($colorMode === 'light' ? `rgba(0, 0, 0, 0.2)` : `rgba(255, 255, 255, 0.2)`)};
    padding: ${({ $hasMainText, $hasContent }) => ($hasMainText ? '8px 12px' : $hasContent ? '8px' : '4px 8px')};
  }

  & .MuiTooltip-arrow {
    color: ${({ $colorMode }) => ($colorMode === 'light' ? Colors.grey1 : Colors.grey9)};
    &::before {
      border: 1px solid ${({ $colorMode }) => ($colorMode === 'light' ? blendWithWhite(Colors.grey2, 0.8) : Colors.grey8)};
    }
  }
`;

interface TooltipProps {
  children: ReactNode;
  content?: ReactNode;
  text?: string;
  sub_text?: string;
  colorMode?: 'light' | 'dark';
  position?: 'top' | 'bottom' | 'left' | 'right';
  open?: boolean;
  onClick?: () => void;
  hide?: boolean;
}

const Tooltip: FunctionComponent<TooltipProps> = ({
  children,
  content,
  text,
  sub_text,
  colorMode = 'dark',
  position = 'bottom',
  open,
  onClick,
  hide = false,
}) => {
  if (hide) {
    return (
      <div style={{ display: 'inline-block', verticalAlign: 'middle', lineHeight: 0 }}>
        {children}
      </div>
    );
  }

  const tooltipContent = (
    <TooltipContent>
      {content && <MainTextOrContent>{content}</MainTextOrContent>}
      {text && <MainTextOrContent>{text}</MainTextOrContent>}
      {sub_text && <SubText $colorMode={colorMode}>{sub_text}</SubText>}
    </TooltipContent>
  );

  return (
    <StyledTooltip
      title={tooltipContent}
      placement={position}
      arrow
      $colorMode={colorMode}
      $hasMainText={!!text}
      $hasContent={!!content}
      open={open}
      onClick={onClick}
      className=""
    >
      <div style={{ display: 'inline-block', verticalAlign: 'middle', lineHeight: 0 }}>
        {children}
      </div>
    </StyledTooltip>
  );
};

export default Tooltip;